(() => {
  const REQUEST_TIMEOUT_MS = 10000;

  const subscribeEmail = async ({ email, phone, firstName, lastName }) => {
    const config = window.SMA_MARKETING || {};
    if (config.proxyEnabled) {
      throw new Error("Please complete phone verification to save your marketing preferences.");
    }
    // Only public identifiers belong in theme settings or browser requests.
    if (!/^[A-Za-z0-9]{6}$/.test(config.klaviyoCompanyId || "") ||
        !/^[A-Za-z0-9]{6}$/.test(config.klaviyoListId || "")) {
      throw new Error("Email signup is temporarily unavailable.");
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(config.klaviyoCompanyId)}`,
        {
          method: "POST",
          credentials: "omit",
          cache: "no-store",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            revision: "2026-07-15",
          },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: "Save Max Auto verified free entry",
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email,
                      ...(phone ? { phone_number: phone } : {}),
                      ...(firstName ? { first_name: firstName } : {}),
                      ...(lastName ? { last_name: lastName } : {}),
                      subscriptions: {
                        email: { marketing: { consent: "SUBSCRIBED" } },
                      },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: "list", id: config.klaviyoListId } },
              },
            },
          }),
        },
      );
      // Acceptance can still require the customer's double opt-in confirmation.
      if (response.status !== 202) {
        throw new Error("Email signup could not be saved.");
      }
    } finally {
      clearTimeout(timeout);
    }
  };

  // Retain the current SMS integration until the Postscript backend is supplied.
  // This endpoint enrolls in Klaviyo SMS; it does not enroll in Postscript.
  const subscribeSms = async ({ email, phone }) => {
    const listId = window.SMA_MARKETING?.kwikListId;
    if (!/^[A-Za-z0-9]{6}$/.test(listId || "")) {
      throw new Error("Text message signup is temporarily unavailable.");
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch("https://api.kwik.dev/api/klaviyo/subscription-optin", {
        method: "POST",
        credentials: "omit",
        signal: controller.signal,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email,
          phone_number: phone,
          list_id: listId,
          consent: ["sms"],
        }),
      });
      if (!response.ok) throw new Error("Text message signup could not be saved.");
    } finally {
      clearTimeout(timeout);
    }
  };

  const subscribeProxy = async (profile) => {
    const config = window.SMA_MARKETING || {};
    const endpoint = config.proxyPath;
    // Restrict credentials to a local app-proxy path, including customized prefixes.
    if (typeof endpoint !== "string" || !/^\/(apps|a|community|tools)\/[a-zA-Z0-9_-]+\/marketing$/.test(endpoint) ||
        typeof profile.accessToken !== "string" || !profile.accessToken.trim() ||
        !/^\+[1-9]\d{7,14}$/.test(profile.phone || "") ||
        !profile.consent || typeof profile.consent.version !== "string" || !profile.consent.version.trim() ||
        profile.channels.some((channel) => !["email", "sms"].includes(channel) ||
          typeof profile.consent[channel] !== "string" || !profile.consent[channel].trim()) ||
        new Set(profile.channels).size !== profile.channels.length || !window.crypto?.randomUUID) {
      throw new Error("Marketing signup is temporarily unavailable.");
    }
    const consent = { version: profile.consent.version };
    for (const channel of profile.channels) consent[channel] = profile.consent[channel];
    const requestId = window.crypto.randomUUID();
    const body = JSON.stringify({ requestId, accessToken: profile.accessToken,
      email: profile.email, phone: profile.phone, channels: profile.channels, consent });
    // Retry one lost response with the same ID. Never fall back to the legacy providers.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(endpoint, { method: "POST", credentials: "same-origin", cache: "no-store", redirect: "error",
          headers: { "Content-Type": "application/json", Accept: "application/json" }, body, signal: controller.signal });
        if (response.status !== 202) {
          if (response.status >= 500 && attempt === 0) continue;
          throw new Error("Marketing preferences could not be saved.");
        }
        const result = await response.json();
        if (!result || result.requestId !== requestId || result.status !== "saved" || !Array.isArray(result.channels) ||
            profile.channels.some((channel) => {
              const entries = result.channels.filter((entry) => entry?.channel === channel);
              return entries.length !== 1 || !["pending", "sending", "accepted"].includes(entries[0].status);
            })) {
          throw new Error("Some marketing preferences could not be saved.");
        }
        return result;
      } catch (error) {
        if (attempt === 0 && ["AbortError", "TypeError", "SyntaxError"].includes(error.name)) continue;
        throw error;
      } finally { clearTimeout(timeout); }
    }
  };

  // Call only after userPhoneSignUp has successfully verified the code.
  const subscribe = async (profile) => {
    if (!profile.channels.length) return;
    if (window.SMA_MARKETING?.proxyEnabled) return subscribeProxy(profile);
    const requests = [];
    if (profile.channels.includes("email")) requests.push(subscribeEmail(profile));
    if (profile.channels.includes("sms")) requests.push(subscribeSms(profile));
    const results = await Promise.allSettled(requests);
    if (results.some((result) => result.status === "rejected")) {
      throw new Error("Some marketing preferences could not be saved.");
    }
  };

  window.SMAFreeEntryMarketing = { subscribe, subscribeEmail };
})();
